import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Network } from 'lucide-react';
import type { DiagramLayers } from '../components/ViewModeToggle';
import { DEFAULT_DIAGRAM_LAYERS } from '../components/ViewModeToggle';
import { useBuildTree } from '../hooks/usePipelineNodes';
import { NodeTreeView } from '../components/NodeTreeView';
import { ENTITY_NODE_TYPES, isSourceNodeType, isTransformationNodeType } from '../services/pipelineNode.service';
import type { BuildTreeNode } from '../services/pipelineNode.service';

/**
 * Entities and the relationships between them are the diagram's baseline; metrics and the
 * pipeline feeding the entities are layers over it. REPORT/DESTINATION are drawn nowhere.
 */
function includeInDiagram(nodeType: string, layers: DiagramLayers): boolean {
  if (ENTITY_NODE_TYPES.includes(nodeType)) return true;
  if (nodeType === 'MEASURE') return layers.metrics;
  if (isSourceNodeType(nodeType) || isTransformationNodeType(nodeType)) return layers.sources;
  return false;
}

/** Where clicking a drawn node lands. Null for anything the Data Model has no page for. */
function detailPathFor(node: BuildTreeNode): string | null {
  if (node.object_type === 'Measure') return `/data-model/metrics/${node.object_id}`;
  if (node.object_type !== 'PipelineNode') return null;
  return ENTITY_NODE_TYPES.includes(node.type)
    ? `/data-model/entities/${node.object_id}`
    : `/data-model/models/${node.object_id}`;
}

interface DiagramProps {
  /** Layers drawn on top of the entity graph. Defaults to entities alone. */
  layers?: DiagramLayers;
}

export default function Diagram({ layers = DEFAULT_DIAGRAM_LAYERS }: DiagramProps = {}) {
  const navigate = useNavigate();

  // Measures are always fetched so switching views is a client-side filter, not a refetch.
  const { data: buildTreeData, isLoading, error } = useBuildTree({
    includeMeasures: true,
  });

  const filteredNodes = useMemo(() => {
    return buildTreeData?.nodes.filter((node) => includeInDiagram(node.type, layers)) || [];
  }, [buildTreeData?.nodes, layers]);

  // Filter edges to only those between visible nodes.
  // Reverse NODE_RELATIONSHIP edges (swap from/to) so FK direction shows child -> parent.
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredNodes.map(n => n.id));
    return (buildTreeData?.edges || [])
      .filter((edge) => visibleNodeIds.has(edge.from_id) && visibleNodeIds.has(edge.to_id))
      .map(e => e.dependency_type === 'NODE_RELATIONSHIP'
        ? { ...e, from_id: e.to_id, to_id: e.from_id }
        : e
      );
  }, [buildTreeData?.edges, filteredNodes]);

  // Clicking a node goes straight to its detail page — no intermediate drawer.
  const handleNodeClick = (objectId: string, objectType?: string) => {
    const matchedNode = filteredNodes.find(
      (n) => n.object_id === objectId && n.object_type === objectType
    );
    if (!matchedNode) return;
    const path = detailPathFor(matchedNode);
    if (path) navigate(path);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading diagram...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">
            Failed to load diagram. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (filteredNodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <div className="flex flex-col items-center gap-4">
          <Network className="h-12 w-12" />
          <p className="text-lg font-medium">Nothing to display</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <NodeTreeView
        nodes={filteredNodes}
        edges={filteredEdges}
        onNodeClick={handleNodeClick}
        className="h-full"
      />
    </div>
  );
}
